import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import BatteryCharging20Icon from '@mui/icons-material/BatteryCharging20'; // Low Saturation
import BatteryCharging60Icon from '@mui/icons-material/BatteryCharging60'; // Moderate Saturation
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull'; // High Saturation
import CallMergeIcon from '@mui/icons-material/CallMerge'; // Convergence
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Concepts Covered
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Contradiction (if needed)
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'; // Novelty
import RepeatIcon from '@mui/icons-material/Repeat'; // Repetition
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'; // Underrepresented
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt'; // Saturation Status
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined'; // Silence
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

// Helper for Saturation Status
const getSaturationStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'high':
      return { icon: <BatteryChargingFullIcon />, color: 'success', label: 'High' };
    case 'moderate':
      return { icon: <BatteryCharging60Icon />, color: 'warning', label: 'Moderate' };
    case 'low':
      return { icon: <BatteryCharging20Icon />, color: 'error', label: 'Low' };
    default:
      return { icon: <SignalCellularAltIcon />, color: 'default', label: status || 'Unknown' };
  }
};

// Helper for Evidence Signal Type
const getSignalIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'repetition':
      return <RepeatIcon fontSize="small" color="info" />;
    case 'convergence':
      return <CallMergeIcon fontSize="small" color="primary" />;
    case 'novelty':
      return <NewReleasesOutlinedIcon fontSize="small" color="success" />;
    case 'silence':
      return <VolumeOffOutlinedIcon fontSize="small" color="disabled" />;
    case 'contradiction':
      return <ErrorOutlineIcon fontSize="small" color="error" />;
    default:
      return null;
  }
};

const SaturationGaugeAnalysisTranscript = ({ summaryData }) => {
  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Saturation Gauge analysis data not available.
        </Box>
      </Card>
    );
  }

  const {
    summary,
    concepts_covered = [],
    underrepresented_perspectives = [],
    saturation_status,
    evidence_signals = [],
  } = summaryData.results;

  const saturationStyle = getSaturationStyle(saturation_status);

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box
        px={2}
        py={1.5}
        bgcolor="grey.100"
        borderBottom={1}
        borderColor="divider"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Saturation Gauge Analysis
          <AnalysisLensHelperText lensName="saturation_gauge" level="transcript" size="small" />
        </Typography>
        <Chip
          icon={saturationStyle.icon}
          label={`Status: ${saturationStyle.label}`}
          color={saturationStyle.color}
          size="small"
        />
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        {summary && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Analysis Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {summary}
            </Typography>
          </Box>
        )}

        {/* Concepts and Perspectives */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Concepts Covered */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <CheckCircleOutlineIcon color="success" /> Concepts Covered
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {concepts_covered.length > 0 ? (
                concepts_covered.map((concept, index) => (
                  <Chip key={`concept-${index}`} label={concept} size="small" />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No concepts listed.
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Underrepresented Perspectives */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ReportProblemOutlinedIcon color="warning" /> Underrepresented Perspectives
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {underrepresented_perspectives.length > 0 ? (
                underrepresented_perspectives.map((perspective, index) => (
                  <Chip
                    key={`perspective-${index}`}
                    label={perspective}
                    size="small"
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No underrepresented perspectives listed.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Evidence Signals */}
        {evidence_signals.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              Evidence Signals for Saturation Status
            </Typography>
            <Stack spacing={2.5}>
              {evidence_signals.map((signal, index) => {
                const Icon = getSignalIcon(signal.signal_type);
                return (
                  <Box
                    key={`signal-${index}`}
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography
                        variant="subtitle2"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {Icon}
                        {signal.signal_type
                          ? signal.signal_type.charAt(0).toUpperCase() + signal.signal_type.slice(1)
                          : 'Signal'}
                      </Typography>
                      <Typography variant="body2" sx={{ pl: Icon ? 3 : 0 }}>
                        {signal.description || 'N/A'}
                      </Typography>
                      {signal.quotes?.length > 0 && (
                        <Box sx={{ pl: Icon ? 3 : 0, pt: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Supporting Quotes:
                          </Typography>
                          <List dense disablePadding>
                            {signal.quotes.map((quote, idx) => (
                              <ListItem
                                key={idx}
                                disableGutters
                                sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 0.5 } }}
                              >
                                <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                                  <FormatQuoteIcon
                                    fontSize="small"
                                    sx={{ transform: 'scaleX(-1)' }}
                                    color="disabled"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={quote}
                                  primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default SaturationGaugeAnalysisTranscript;
